import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { supabase } from '../config/supabase';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { getEntity } from '../data/entities';
import { assignmentRoleOptions, teamColorOptions } from '../data/options';
import { lookupLabel } from '../data/lookups';
import { teamAccentColor } from '../utils/teamColors';
import { detectPermissionPreset, legacyAssignmentMatrix, matrixToAssignmentPatch } from '../utils/permissions';
import { groupMeReminderOptions, normalizeGroupMeReminders } from '../utils/groupmeReminders';
import type { FieldConfig, LookupState, RowRecord, TeamCaptainAssignmentRecord, TeamColor, UserPreferences } from '../types';
import { RosterManager } from './RosterManager';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { TeamIcon } from './TeamIcon';

// Compile-time gate: the wizard and saved roster fixtures are absent from hosted builds.
const DevelopmentSeasonSetupEntry = import.meta.env.COURTCAPTAIN_ENV === 'development'
  ? lazy(() => import('../dev/season-setup/TeamSeasonSetupEntry'))
  : null;

const StagingSeasonSetupEntry = import.meta.env.COURTCAPTAIN_ENV === 'staging'
  ? lazy(() => import('./SeasonSetupDashboard').then(m => ({ default: m.SeasonSetupDashboard })))
  : null;

type TeamManagerProps = {
  lookups: LookupState;
  refreshLookups: () => Promise<void>;
  onViewTeamMatches: (teamId: string) => void;
  userPreferences: UserPreferences;
  onUserPreferencesChange: (patch: Partial<UserPreferences>) => void;
  initialTeamId?: string | null;
  readOnly?: boolean;
  onBack?: () => void;
  canViewPartnerPreferences?: boolean;
  canEditPartnerPreferences?: boolean;
  canManageTeamAccess?: boolean;
};

type TeamRow = RowRecord & {
  id: string;
  team_name: string;
  short_name?: string | null;
  team_icon_url?: string | null;
  season_label: string | null;
  organization_id: string | null;
  league_configuration_id: string | null;
  team_flight: string | null;
  team_gender_type: string | null;
  player_rating_basis: string | null;
  home_facility_id: string | null;
  player_portal_access_enabled?: boolean | null;
  player_event_self_service_enabled?: boolean | null;
  player_portal_tentative_lineups_enabled?: boolean | null;
  player_portal_tentative_lineup_lookahead_matches?: number | null;
  default_groupme_event_reminders?: number[] | null;
  is_active: boolean;
};

type CaptainAssignment = {
  id: string;
  team_id: string;
  app_user_id: string;
  assignment_role: TeamCaptainAssignmentRecord['assignment_role'];
  is_primary_captain: boolean;
  is_active: boolean;
  notes: string | null;
  created_at: string | null;
  created_by: string | null;
};

type UserOption = {
  id: string;
  display_name: string;
  email: string | null;
  is_active: boolean;
};

type TeamSummary = {
  rosterCount: number;
  accessCount: number;
  captainCount: number;
  captainNames: string[];
  coCaptainNames: string[];
};

type Snack = { severity: 'success' | 'error'; message: string };
type TeamTab = 'overview' | 'lineup_recommendations' | 'roster' | 'partner_preferences' | 'fees_payments' | 'access';
type TeamStatusFilter = 'active' | 'inactive' | 'all';
type TcdIntegrationStatus = {
  configured: boolean;
  serverConfigured: boolean;
  missingConfigKeys?: string[];
  supabaseProjectRef?: string | null;
  enabled: boolean;
  teamUrl: string;
  credentialsConfigured: boolean;
  lastTestedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
};
type TcdDiagnosticResult = {
  success?: boolean;
  message?: string;
  error?: string;
  diagnostics?: {
    authenticationSuccessful?: boolean;
    teamOverviewRetrieved?: boolean;
    rosterAvailabilityRetrieved?: boolean;
    rosterAvailabilityMechanism?: string | null;
    sessionCookieEstablished?: boolean;
    editMyAvailabilityFound?: boolean;
  };
  navigation?: {
    availabilityCandidates?: Array<{ text?: string; href?: string | null; score?: number }>;
  };
  parsed?: {
    team?: { name?: string | null };
    players?: Array<{ name: string }>;
    matches?: Array<{
      date: string | null;
      opponent?: string | null;
      rawHeader?: string | null;
      players: Array<{ name: string; availability: string; rawAvailability: string }>;
    }>;
  };
  comparison?: {
    players?: { exact: number; possible: number; unmatched: number };
    matches?: { matched: number; possible: number; unmatched: number };
  } | null;
};
type GroupMeIntegrationStatus = {
  tokenConfigured: boolean;
  serverConfigured: boolean;
  missingConfigKeys?: string[];
  supabaseProjectRef?: string | null;
  enabled: boolean;
  teamConfigured: boolean;
  teamTokenUserId: string | null;
  lastTestedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  lastSyncedAt: string | null;
  fallbackAvailable: boolean;
};
type GroupMeGroupOption = {
  id: string;
  name: string;
  mappedTeamId: string | null;
  mappedTeamName: string | null;
  available: boolean;
};
type GroupMeBotOption = { id: string; name: string; groupId: string; active: boolean };
type GroupMeTeamUpdatesSettings = {
  botId: string | null;
  botName: string | null;
  botStatus: 'unconfigured' | 'connected' | 'missing' | 'multiple' | 'invalid';
  botValidatedAt: string | null;
  botValidationError: string | null;
  scheduleEnabled: boolean;
  scheduleDayOfWeek: number;
  scheduleTime: string;
  scheduleLookaheadDays: number;
  lineupMode: 'disabled' | 'manual_only' | 'scheduled';
  lineupLookaheadDays: number;
  timezone: string;
};
type GroupMeTeamUpdatePreview = {
  type: 'upcoming_schedule' | 'upcoming_lineup';
  message?: string;
  destination?: string;
  bot?: string;
  noContent?: boolean;
  unchanged?: boolean;
  warning?: string;
  matchId?: string;
};
type TeamManagerViewState = {
  selectedTeamId: string;
  teamStatusFilter: TeamStatusFilter;
  activeTab: TeamTab;
  accessSearch: string;
  accessRoleFilter: string;
  accessStatusFilter: string;
  accessPrimaryFilter: string;
  showInactiveAccess: boolean;
};

const teamTabLabels: Record<TeamTab, string> = {
  overview: 'Overview',
  lineup_recommendations: 'Lineup Recommendations',
  roster: 'Roster',
  partner_preferences: 'Partner Preferences',
  fees_payments: 'Fees & Payments',
  access: 'Access',
};

type TeamPaymentMethod = {
  id: string;
  team_id: string;
  payment_method: 'Venmo' | 'Zelle' | 'PayPal' | 'Cash App' | 'Other';
  custom_label: string | null;
  payment_identifier: string;
  is_enabled: boolean;
  sort_order: number;
};

const overviewSections: Array<{ title: string; description: string; fields: string[] }> = [
  { title: 'Team Identity', description: 'Team ownership, optional season label, and status.', fields: ['team_name', 'short_name', 'organization_id', 'season_label', 'team_color', 'is_active'] },
  { title: 'Player Portal', description: 'Player access and team-specific tentative lineup visibility.', fields: ['player_portal_access_enabled', 'player_event_self_service_enabled', 'player_portal_tentative_lineups_enabled', 'player_portal_tentative_lineup_lookahead_matches'] },
  { title: 'League and Competition', description: 'League-specific details used by scheduling, scoresheets, and recommendations.', fields: ['league_name', 'team_gender_type', 'league_configuration_id', 'advancement_type', 'player_rating_basis', 'team_flight', 'usta_team_number', 'team_page_url'] },
  { title: 'Match Defaults', description: 'Defaults used when creating matches and roster expectations.', fields: ['home_facility_id', 'default_warmup_minutes', 'default_target_match_count_per_player'] },
  { title: 'Communication', description: 'Messaging and GroupMe setup for this team.', fields: ['groupme_group_id', 'groupme_event_reminders_enabled', 'default_groupme_event_reminders', 'home_match_opponent_message_template'] },
  { title: 'Notes', description: 'Internal notes for captains and admins.', fields: ['notes'] },
];

function captainSaveErrorMessage(message: string): string {
  if (message.includes('team_captain_assignments_role_check')) {
    return 'Your database needs the Viewer access role update. Run supabase/sql-editor-viewer-access-role.sql in the Supabase SQL Editor, then try again.';
  }
  return message;
}

function roleLabel(value: string): string {
  if (value === 'assistant_captain') return 'Assistant Captain';
  return assignmentRoleOptions.find((option) => option.value === value)?.label || value;
}

function primaryCaptainApplies(role: string): boolean {
  return role === 'captain' || role === 'co_captain';
}

function userLabel(users: UserOption[], userId: string): string {
  const user = users.find((candidate) => candidate.id === userId);
  if (!user) return userId;
  return `${user.display_name}${user.email ? ` (${user.email})` : ''}`;
}

function emptyValue(field: FieldConfig): string | number | boolean | null {
  if (field.type === 'boolean') return false;
  if (field.type === 'number') return null;
  return '';
}

function initialTeamForm(row?: TeamRow, workspaceId?: string): RowRecord {
  const entity = getEntity('teams');
  return entity.fields.reduce<RowRecord>((acc, field) => {
    const existing = row?.[field.name];
    if (existing !== undefined && existing !== null) acc[field.name] = existing;
    else if (field.name === 'is_active') acc[field.name] = true;
    else if (field.name === 'player_portal_access_enabled') acc[field.name] = true;
    else if (field.name === 'player_event_self_service_enabled') acc[field.name] = false;
    else if (field.name === 'player_portal_tentative_lineups_enabled') acc[field.name] = false;
    else if (field.name === 'player_portal_tentative_lineup_lookahead_matches') acc[field.name] = 3;
    else if (field.name === 'team_gender_type') acc[field.name] = 'mixed';
    else if (field.name === 'team_color') acc[field.name] = 'gray';
    else if (field.name === 'player_rating_basis') acc[field.name] = 'edr';
    else if (field.name === 'enforce_target_matches' || field.name === 'enforce_max_sit') acc[field.name] = false;
    else if (field.name === 'target_match_variance') acc[field.name] = 1;
    else if (field.name === 'max_weeks_without_match') acc[field.name] = 2;
    else if (field.name === 'use_partner_compatibility' || field.name === 'use_partner_scheduling_preferences') acc[field.name] = true;
    else if (field.name === 'default_warmup_minutes') acc[field.name] = 30;
    else if (field.name === 'default_groupme_event_reminders') acc[field.name] = [];
    else if (field.name === 'workspace_id' && workspaceId) acc[field.name] = workspaceId;
    else acc[field.name] = emptyValue(field);
    return acc;
  }, {});
}

function normalizeTeamForm(form: RowRecord): RowRecord {
  const entity = getEntity('teams');
  return entity.fields.reduce<RowRecord>((payload, field) => {
    const value = form[field.name];
    if (field.type === 'number' || field.name === 'target_match_variance') payload[field.name] = value === '' || value === null || value === undefined ? null : Number(value);
    else if (field.type === 'boolean') payload[field.name] = Boolean(value);
    else if (field.name === 'default_groupme_event_reminders') payload[field.name] = normalizeGroupMeReminders(value);
    else if (field.name === 'team_flight' || field.name === 'short_name') payload[field.name] = typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
    else payload[field.name] = value === '' ? null : value;
    return payload;
  }, {});
}

function isValidUrl(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isOptionalNonNegativeInteger(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue >= 0;
}

function countByTeam<T extends { team_id: string }>(rows: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  rows.forEach((row) => map.set(row.team_id, [...(map.get(row.team_id) || []), row]));
  return map;
}

function teamTypeLabel(value: string | null | undefined): string {
  if (value === 'mens') return "Men's";
  if (value === 'womens') return "Women's";
  if (value === 'mixed') return 'Mixed';
  return 'Team';
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

async function authHeaders(contentType = false): Promise<Record<string, string>> {
  const session = (await supabase.auth.getSession()).data.session;
  return {
    ...(contentType ? { 'Content-Type': 'application/json' } : {}),
    ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

function formValueForCompare(value: unknown): unknown {
  return value === '' ? null : value;
}

function joinNames(names: string[]): string {
  if (names.length === 0) return 'None assigned';
  return names.join(', ');
}

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function TeamManager({ lookups, refreshLookups, onViewTeamMatches, userPreferences, onUserPreferencesChange, initialTeamId = null, readOnly = false, onBack, canViewPartnerPreferences = true, canEditPartnerPreferences = true, canManageTeamAccess = false }: TeamManagerProps) {
  const auditUserId = useAuditUserId();
  const seasonAccess = useAuth();
  const teamEntity = getEntity('teams');
  const defaultViewState = useMemo<TeamManagerViewState>(() => ({
    selectedTeamId: '',
    teamStatusFilter: 'active',
    activeTab: 'overview',
    accessSearch: '',
    accessRoleFilter: 'all',
    accessStatusFilter: 'active',
    accessPrimaryFilter: 'all',
    showInactiveAccess: false,
  }), []);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'teams',
    defaultState: defaultViewState,
  });
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [teamIconSaving, setTeamIconSaving] = useState(false);
  const [teamSummaries, setTeamSummaries] = useState<Map<string, TeamSummary>>(() => new Map());
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId || savedViewState.selectedTeamId);
  const [teamStatusFilter, setTeamStatusFilter] = useState<TeamStatusFilter>(savedViewState.teamStatusFilter);
  const availableTabs = useMemo<TeamTab[]>(() => ['overview', 'lineup_recommendations', 'roster', ...(canViewPartnerPreferences ? ['partner_preferences' as TeamTab] : []), 'fees_payments', ...(canManageTeamAccess ? ['access' as TeamTab] : [])], [canManageTeamAccess, canViewPartnerPreferences]);
  const [activeTab, setActiveTab] = useState<TeamTab>(availableTabs.includes(savedViewState.activeTab) ? savedViewState.activeTab : 'overview');
  const [teamForm, setTeamForm] = useState<RowRecord>(() => initialTeamForm(undefined, lookups.workspaces[0]?.value));
  const [captainAssignments, setCaptainAssignments] = useState<CaptainAssignment[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [captainsLoading, setCaptainsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<CaptainAssignment | null>(null);
  const [captainForm, setCaptainForm] = useState({
    app_user_id: '',
    assignment_role: 'captain' as TeamCaptainAssignmentRecord['assignment_role'],
    is_primary_captain: false,
    is_active: true,
    notes: '',
  });
  const [accessSearch, setAccessSearch] = useState(savedViewState.accessSearch);
  const [accessRoleFilter, setAccessRoleFilter] = useState(savedViewState.accessRoleFilter);
  const [accessStatusFilter, setAccessStatusFilter] = useState(savedViewState.accessStatusFilter);
  const [accessPrimaryFilter, setAccessPrimaryFilter] = useState(savedViewState.accessPrimaryFilter);
  const [showInactiveAccess, setShowInactiveAccess] = useState(savedViewState.showInactiveAccess);
  const [teamColorPickerOpen, setTeamColorPickerOpen] = useState(false);
  const [tcdStatus, setTcdStatus] = useState<TcdIntegrationStatus | null>(null);
  const [tcdForm, setTcdForm] = useState({ teamUrl: '', username: '', password: '', enabled: false });
  const [tcdLoading, setTcdLoading] = useState(false);
  const [tcdSaving, setTcdSaving] = useState(false);
  const [tcdTesting, setTcdTesting] = useState(false);
  const [tcdDiagnostic, setTcdDiagnostic] = useState<TcdDiagnosticResult | null>(null);
  const [groupMeStatus, setGroupMeStatus] = useState<GroupMeIntegrationStatus | null>(null);
  const [groupMeForm, setGroupMeForm] = useState({ accessToken: '', enabled: false });
  const [groupMeLoading, setGroupMeLoading] = useState(false);
  const [groupMeSaving, setGroupMeSaving] = useState(false);
  const [groupMeTesting, setGroupMeTesting] = useState(false);
  const [groupMeGroups, setGroupMeGroups] = useState<GroupMeGroupOption[]>([]);
  const [groupMeGroupsLoading, setGroupMeGroupsLoading] = useState(false);
  const [teamUpdatesSettings, setTeamUpdatesSettings] = useState<GroupMeTeamUpdatesSettings | null>(null);
  const [teamUpdatesBots, setTeamUpdatesBots] = useState<GroupMeBotOption[]>([]);
  const [teamUpdatesLoading, setTeamUpdatesLoading] = useState(false);
  const [teamUpdatesSaving, setTeamUpdatesSaving] = useState(false);
  const [teamUpdatesPreview, setTeamUpdatesPreview] = useState<GroupMeTeamUpdatePreview | null>(null);
  const [teamUpdatesLineupMatchId, setTeamUpdatesLineupMatchId] = useState('');
  const [teamUpdatesLineupMatches, setTeamUpdatesLineupMatches] = useState<Array<{ id: string; opponent_name: string | null; match_date: string; lineup_status: string }>>([]);
  const [paymentMethods, setPaymentMethods] = useState<TeamPaymentMethod[]>([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodSaving, setPaymentMethodSaving] = useState(false);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    payment_method: 'Venmo' as TeamPaymentMethod['payment_method'],
    custom_label: '',
    payment_identifier: '',
    is_enabled: true,
    sort_order: 0,
  });
  const [snack, setSnack] = useState<Snack | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  const selectedTeam = useMemo(() => teams.find((team) => team.id === selectedTeamId) || null, [teams, selectedTeamId]);
  const selectedSummary = selectedTeamId ? teamSummaries.get(selectedTeamId) : undefined;
  const visibleTeams = useMemo(() => teams.filter((team) => {
    if (teamStatusFilter === 'all') return true;
    if (teamStatusFilter === 'inactive') return team.is_active === false;
    return team.is_active !== false;
  }), [teamStatusFilter, teams]);
  const teamFormDirty = useMemo(() => {
    if (!selectedTeam && !selectedTeamId) return Object.values(teamForm).some((value) => value !== '' && value !== null && value !== false);
    if (!selectedTeam) return false;
    return teamEntity.fields.some((field) => formValueForCompare(teamForm[field.name]) !== formValueForCompare(selectedTeam[field.name]));
  }, [selectedTeam, selectedTeamId, teamEntity.fields, teamForm]);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'teams', ready: !teamsLoading && !captainsLoading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedTeamId(initialTeamId || savedViewState.selectedTeamId);
    setTeamStatusFilter(savedViewState.teamStatusFilter);
    setActiveTab(availableTabs.includes(savedViewState.activeTab) ? savedViewState.activeTab : 'overview');
    setAccessSearch(savedViewState.accessSearch);
    setAccessRoleFilter(savedViewState.accessRoleFilter);
    setAccessStatusFilter(savedViewState.accessStatusFilter);
    setAccessPrimaryFilter(savedViewState.accessPrimaryFilter);
    setShowInactiveAccess(savedViewState.showInactiveAccess);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, availableTabs, initialTeamId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ selectedTeamId, teamStatusFilter, activeTab, accessSearch, accessRoleFilter, accessStatusFilter, accessPrimaryFilter, showInactiveAccess });
  }, [accessPrimaryFilter, accessRoleFilter, accessSearch, accessStatusFilter, activeTab, selectedTeamId, setSavedViewState, showInactiveAccess, teamStatusFilter, viewStateKey, viewStateReady]);

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    const [teamsResult, rosterResult, accessResult, usersResult] = await Promise.all([
      supabase.from('teams').select('*').is('deleted_at', null).order('team_name'),
      supabase.from('team_rosters').select('team_id').eq('is_active', true).is('deleted_at', null),
      supabase.from('team_captain_assignments').select('team_id, app_user_id, assignment_role, is_active').eq('is_active', true).is('deleted_at', null),
      supabase.from('app_users').select('id, display_name').is('deleted_at', null),
    ]);
    setTeamsLoading(false);
    const error = teamsResult.error || rosterResult.error || accessResult.error || usersResult.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const nextTeams = (teamsResult.data || []) as unknown as TeamRow[];
    const rosterByTeam = countByTeam((rosterResult.data || []) as Array<{ team_id: string }>);
    const userNamesById = new Map(((usersResult.data || []) as Array<{ id: string; display_name: string | null }>).map((user) => [user.id, user.display_name || user.id]));
    const accessByTeam = countByTeam((accessResult.data || []) as Array<{ team_id: string; app_user_id: string; assignment_role: string; is_active: boolean }>);
    setTeamSummaries(new Map(nextTeams.map((team) => {
      const accessRows = accessByTeam.get(team.id) || [];
      return [team.id, {
        rosterCount: rosterByTeam.get(team.id)?.length || 0,
        accessCount: accessRows.length,
        captainCount: accessRows.filter((row) => ['captain', 'co_captain', 'assistant_captain'].includes(row.assignment_role)).length,
        captainNames: accessRows.filter((row) => row.assignment_role === 'captain').map((row) => userNamesById.get(row.app_user_id) || row.app_user_id).sort((a, b) => a.localeCompare(b)),
        coCaptainNames: accessRows.filter((row) => row.assignment_role === 'co_captain').map((row) => userNamesById.get(row.app_user_id) || row.app_user_id).sort((a, b) => a.localeCompare(b)),
      }];
    })));
    setTeams(nextTeams);
    const nextVisibleTeams = nextTeams.filter((team) => {
      if (teamStatusFilter === 'all') return true;
      if (teamStatusFilter === 'inactive') return team.is_active === false;
      return team.is_active !== false;
    });
    setSelectedTeamId((current) => (current && nextVisibleTeams.some((team) => team.id === current) ? current : nextVisibleTeams[0]?.id || ''));
  }, [teamStatusFilter]);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    if (!initialTeamId || !teams.some((team) => team.id === initialTeamId)) return;
    setSelectedTeamId(initialTeamId);
    setActiveTab('overview');
  }, [initialTeamId, teams]);

  useEffect(() => {
    setTeamForm(initialTeamForm(selectedTeam || undefined, lookups.workspaces[0]?.value));
  }, [lookups.workspaces, selectedTeam]);

  const loadPaymentMethods = useCallback(async () => {
    if (!selectedTeamId) {
      setPaymentMethods([]);
      return;
    }
    setPaymentMethodsLoading(true);
    const result = await supabase
      .from('team_payment_methods')
      .select('id, team_id, payment_method, custom_label, payment_identifier, is_enabled, sort_order')
      .eq('team_id', selectedTeamId)
      .is('deleted_at', null)
      .order('sort_order')
      .order('created_at');
    setPaymentMethodsLoading(false);
    if (result.error) {
      setPaymentMethods([]);
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    setPaymentMethods((result.data || []) as TeamPaymentMethod[]);
  }, [selectedTeamId]);

  useEffect(() => {
    if (activeTab === 'fees_payments') void loadPaymentMethods();
  }, [activeTab, loadPaymentMethods]);

  const addPaymentMethod = async () => {
    if (readOnly || !selectedTeamId) return;
    const identifier = paymentMethodForm.payment_identifier.trim();
    const customLabel = paymentMethodForm.custom_label.trim();
    if (!identifier) {
      setSnack({ severity: 'error', message: 'Payment identifier is required.' });
      return;
    }
    if (paymentMethodForm.payment_method === 'Other' && !customLabel) {
      setSnack({ severity: 'error', message: 'Enter a label for Other.' });
      return;
    }
    setPaymentMethodSaving(true);
    const result = await supabase.from('team_payment_methods').insert({
      team_id: selectedTeamId,
      payment_method: paymentMethodForm.payment_method,
      custom_label: paymentMethodForm.payment_method === 'Other' ? customLabel : null,
      payment_identifier: identifier,
      is_enabled: paymentMethodForm.is_enabled,
      sort_order: Math.max(0, Number(paymentMethodForm.sort_order) || 0),
      created_by: auditUserId,
      updated_by: auditUserId,
    });
    setPaymentMethodSaving(false);
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    setPaymentMethodForm({ payment_method: 'Venmo', custom_label: '', payment_identifier: '', is_enabled: true, sort_order: paymentMethods.length + 1 });
    setSnack({ severity: 'success', message: 'Payment method added.' });
    await loadPaymentMethods();
  };

  const updatePaymentMethod = async (method: TeamPaymentMethod, patch: Partial<TeamPaymentMethod>) => {
    if (readOnly) return;
    const result = await supabase.from('team_payment_methods').update({ ...patch, updated_by: auditUserId }).eq('id', method.id);
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    await loadPaymentMethods();
  };

  const deletePaymentMethod = async (method: TeamPaymentMethod) => {
    if (readOnly || !window.confirm(`Delete ${method.payment_method} payment method?`)) return;
    const result = await supabase.from('team_payment_methods').update({ deleted_at: new Date().toISOString(), deleted_by: auditUserId, updated_by: auditUserId }).eq('id', method.id);
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Payment method deleted.' });
    await loadPaymentMethods();
  };

  const loadTcdStatus = useCallback(async () => {
    if (!selectedTeamId) {
      setTcdStatus(null);
      setTcdForm({ teamUrl: '', username: '', password: '', enabled: false });
      return;
    }
    setTcdLoading(true);
    const response = await fetch(`/api/tcd/integration/status?teamId=${encodeURIComponent(selectedTeamId)}`, {
      headers: await authHeaders(),
    });
    const result = await response.json() as TcdIntegrationStatus & { error?: string };
    setTcdLoading(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to load TCD integration status.' });
      return;
    }
    setTcdStatus(result);
    setTcdForm({ teamUrl: result.teamUrl || '', username: '', password: '', enabled: Boolean(result.enabled) });
  }, [selectedTeamId]);

  useEffect(() => {
    void loadTcdStatus();
  }, [loadTcdStatus]);

  const loadGroupMeStatus = useCallback(async () => {
    if (!selectedTeamId) {
      setGroupMeStatus(null);
      setGroupMeForm({ accessToken: '', enabled: false });
      return;
    }
    setGroupMeLoading(true);
    const response = await fetch(`/api/groupme/integration/status?teamId=${encodeURIComponent(selectedTeamId)}`, {
      headers: await authHeaders(),
    });
    const result = await response.json() as GroupMeIntegrationStatus & { error?: string };
    setGroupMeLoading(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to load GroupMe integration status.' });
      return;
    }
    setGroupMeStatus(result);
    setGroupMeForm({ accessToken: '', enabled: Boolean(result.enabled) });
  }, [selectedTeamId]);

  useEffect(() => {
    void loadGroupMeStatus();
  }, [loadGroupMeStatus]);

  const loadGroupMeGroups = useCallback(async () => {
    if (!selectedTeamId) {
      setGroupMeGroups([]);
      return;
    }
    setGroupMeGroupsLoading(true);
    const response = await fetch(`/api/groupme/integration/groups?teamId=${encodeURIComponent(selectedTeamId)}`, {
      headers: await authHeaders(),
    });
    const result = await response.json() as { groups?: GroupMeGroupOption[]; error?: string };
    setGroupMeGroupsLoading(false);
    if (!response.ok) {
      setGroupMeGroups([]);
      return;
    }
    setGroupMeGroups(result.groups || []);
  }, [selectedTeamId]);

  useEffect(() => {
    if (!groupMeStatus?.tokenConfigured && !groupMeStatus?.fallbackAvailable) {
      setGroupMeGroups([]);
      return;
    }
    void loadGroupMeGroups();
  }, [groupMeStatus?.fallbackAvailable, groupMeStatus?.tokenConfigured, loadGroupMeGroups]);

  const loadTeamUpdates = useCallback(async () => {
    if (!selectedTeamId) {
      setTeamUpdatesSettings(null);
      setTeamUpdatesBots([]);
      return;
    }
    setTeamUpdatesLoading(true);
    const response = await fetch(`/api/groupme/team-updates/status?teamId=${encodeURIComponent(selectedTeamId)}`, { headers: await authHeaders() });
    const result = await response.json() as { settings?: GroupMeTeamUpdatesSettings; error?: string };
    setTeamUpdatesLoading(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to load GroupMe Team Updates.' });
      return;
    }
    setTeamUpdatesSettings(result.settings || null);
  }, [selectedTeamId]);

  useEffect(() => { void loadTeamUpdates(); }, [loadTeamUpdates]);

  useEffect(() => {
    if (!selectedTeamId) { setTeamUpdatesLineupMatches([]); setTeamUpdatesLineupMatchId(''); return; }
    void (async () => {
      const result = await supabase.from('match_events').select('id, opponent_name, match_date, lineup_status').eq('team_id', selectedTeamId).eq('lineup_status', 'Published').in('match_status', ['Scheduled', 'Rescheduled', 'Postponed']).is('deleted_at', null).order('match_date');
      setTeamUpdatesLineupMatches((result.data || []) as Array<{ id: string; opponent_name: string | null; match_date: string; lineup_status: string }>);
    })();
  }, [selectedTeamId]);

  const loadCaptains = useCallback(async () => {
    if (!selectedTeamId) {
      setCaptainAssignments([]);
      return;
    }
    setCaptainsLoading(true);
    const [assignmentsResult, usersResult] = await Promise.all([
      supabase
        .from('team_captain_assignments')
        .select('id, team_id, app_user_id, assignment_role, is_primary_captain, is_active, notes, created_at, created_by')
        .eq('team_id', selectedTeamId)
        .is('deleted_at', null)
        .order('is_primary_captain', { ascending: false })
        .order('created_at', { ascending: true }),
      supabase
        .from('app_users')
        .select('id, display_name, email, is_active')
        .is('deleted_at', null)
        .order('display_name'),
    ]);
    setCaptainsLoading(false);

    const error = assignmentsResult.error || usersResult.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setCaptainAssignments((assignmentsResult.data || []) as unknown as CaptainAssignment[]);
    setUsers((usersResult.data || []) as unknown as UserOption[]);
  }, [selectedTeamId]);

  useEffect(() => {
    if (activeTab === 'access') void loadCaptains();
  }, [activeTab, loadCaptains]);

  const availableUsers = useMemo(() => {
    const activeUsers = users.filter((user) => user.is_active);
    if (editingAssignment) return activeUsers;
    const activeAssignedUserIds = new Set(captainAssignments.filter((assignment) => assignment.is_active).map((assignment) => assignment.app_user_id));
    return activeUsers.filter((user) => !activeAssignedUserIds.has(user.id));
  }, [users, captainAssignments, editingAssignment]);

  const userById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);

  const accessSummary = useMemo(() => {
    const activeAssignments = captainAssignments.filter((assignment) => assignment.is_active);
    return {
      total: captainAssignments.length,
      captains: activeAssignments.filter((assignment) => assignment.assignment_role === 'captain').length,
      coCaptains: activeAssignments.filter((assignment) => assignment.assignment_role === 'co_captain').length,
      assistants: activeAssignments.filter((assignment) => assignment.assignment_role === 'assistant_captain').length,
      viewers: activeAssignments.filter((assignment) => assignment.assignment_role === 'viewer').length,
      active: activeAssignments.length,
    };
  }, [captainAssignments]);

  const filteredCaptainAssignments = useMemo(() => {
    const normalizedSearch = accessSearch.trim().toLowerCase();
    return captainAssignments.filter((assignment) => {
      const user = userById.get(assignment.app_user_id);
      if (!showInactiveAccess && !assignment.is_active) return false;
      if (accessRoleFilter !== 'all' && assignment.assignment_role !== accessRoleFilter) return false;
      if (accessStatusFilter === 'active' && !assignment.is_active) return false;
      if (accessStatusFilter === 'inactive' && assignment.is_active) return false;
      if (accessPrimaryFilter === 'primary' && !assignment.is_primary_captain) return false;
      if (accessPrimaryFilter === 'not_primary' && assignment.is_primary_captain) return false;
      if (!normalizedSearch) return true;
      const haystack = `${user?.display_name || ''} ${user?.email || ''} ${assignment.notes || ''}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [accessPrimaryFilter, accessRoleFilter, accessSearch, accessStatusFilter, captainAssignments, showInactiveAccess, userById]);

  const selectTeam = (teamId: string, tab: TeamTab = activeTab) => {
    if (teamFormDirty && !window.confirm('Discard unsaved team changes?')) return;
    setSelectedTeamId(teamId);
    setActiveTab(tab);
  };

  const setTeamFilter = (nextFilter: TeamStatusFilter) => {
    const nextVisibleTeams = teams.filter((team) => {
      if (nextFilter === 'all') return true;
      if (nextFilter === 'inactive') return team.is_active === false;
      return team.is_active !== false;
    });
    setTeamStatusFilter(nextFilter);
    setSelectedTeamId((current) => (current && nextVisibleTeams.some((team) => team.id === current) ? current : nextVisibleTeams[0]?.id || ''));
  };

  const createTeam = () => {
    if (readOnly) return;
    if (teamFormDirty && !window.confirm('Discard unsaved team changes?')) return;
    setSelectedTeamId('');
    setActiveTab('overview');
    setTeamForm(initialTeamForm(undefined, lookups.workspaces[0]?.value));
  };

  const validateTeamForm = (): string | null => {
    const missingRequiredField = teamEntity.fields.find((field) => field.required && (teamForm[field.name] === '' || teamForm[field.name] === null || teamForm[field.name] === undefined));
    if (missingRequiredField) return `${missingRequiredField.label} is required.`;
    const invalidUrlField = teamEntity.fields.find((field) => field.type === 'url' && !isValidUrl(teamForm[field.name]));
    if (invalidUrlField) return `${invalidUrlField.label} must be a valid URL.`;
    if (typeof teamForm.team_flight === 'string' && teamForm.team_flight.trim().length > 30) return 'TCD Flight must be 30 characters or fewer.';
    if (!isOptionalNonNegativeInteger(teamForm.default_target_match_count_per_player)) return 'Default Target Matches Per Player must be a whole number zero or greater.';
    if (!isOptionalNonNegativeInteger(teamForm.default_warmup_minutes)) return 'Default Warmup Minutes must be a whole number zero or greater.';
    if (teamForm.default_warmup_minutes !== '' && teamForm.default_warmup_minutes !== null && Number(teamForm.default_warmup_minutes) > 120) return 'Default Warmup Minutes should be 120 or less.';
    const tentativeLookahead = Number(teamForm.player_portal_tentative_lineup_lookahead_matches);
    if (!Number.isInteger(tentativeLookahead) || tentativeLookahead < 1 || tentativeLookahead > 20) return 'Tentative Lineup Lookahead must be a whole number from 1 to 20 matches.';
    const selectedGroupId = String(teamForm.groupme_group_id || '');
    const selectedGroup = groupMeGroups.find((group) => group.id === selectedGroupId);
    if (selectedGroup && !selectedGroup.available) return 'This GroupMe group is already mapped to another active team.';
    return null;
  };
  const teamValidationError = validateTeamForm();

  useEffect(() => {
    if (!teamFormDirty) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [teamFormDirty]);

  const setTeamTab = (nextTab: TeamTab) => {
    if ((activeTab === 'overview' || activeTab === 'lineup_recommendations') && teamFormDirty && nextTab !== activeTab && !window.confirm('Discard unsaved team changes?')) return;
    setActiveTab(nextTab);
  };

  const discardTeamChanges = () => {
    setTeamForm(initialTeamForm(selectedTeam || undefined, lookups.workspaces[0]?.value));
  };

  const selectedTeamIndex = visibleTeams.findIndex((team) => team.id === selectedTeamId);
  const selectAdjacentTeam = (direction: -1 | 1) => {
    if (visibleTeams.length === 0) return;
    const currentIndex = selectedTeamIndex >= 0 ? selectedTeamIndex : 0;
    const nextIndex = Math.max(0, Math.min(visibleTeams.length - 1, currentIndex + direction));
    selectTeam(visibleTeams[nextIndex].id, activeTab);
  };

  const saveTeam = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    const validationError = validateTeamForm();
    if (validationError) {
      setSnack({ severity: 'error', message: validationError });
      return;
    }
    const payload = normalizeTeamForm(teamForm);
    const result = selectedTeamId
      ? await supabase.from('teams').update({ ...payload, updated_by: auditUserId }).eq('id', selectedTeamId)
      : await supabase.from('teams').insert({ ...payload, created_by: auditUserId, updated_by: auditUserId }).select('id').single();
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    const newTeamId = selectedTeamId || String((result.data as { id: string } | null)?.id || '');
    setSnack({ severity: 'success', message: 'Team saved.' });
    await loadTeams();
    await refreshLookups();
    if (newTeamId) setSelectedTeamId(newTeamId);
  };

  const teamIconPath = (url?: string | null): string | null => {
    if (!url) return null;
    const marker = '/storage/v1/object/public/team-icons/';
    const index = url.indexOf(marker);
    return index < 0 ? null : decodeURIComponent(url.slice(index + marker.length).split('?')[0]);
  };

  const saveTeamIcon = async (file: File | null) => {
    if (!selectedTeamId || readOnly) return;
    if (file && (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
      setSnack({ severity: 'error', message: 'Choose a PNG, JPEG, or WebP image up to 5 MB.' });
      return;
    }
    setTeamIconSaving(true);
    const extension = file?.type === 'image/png' ? 'png' : file?.type === 'image/webp' ? 'webp' : 'jpg';
    const path = file ? `${selectedTeamId}/${crypto.randomUUID()}.${extension}` : null;
    if (file && path) {
      const upload = await supabase.storage.from('team-icons').upload(path, file, { contentType: file.type, upsert: false });
      if (upload.error) {
        setTeamIconSaving(false);
        setSnack({ severity: 'error', message: upload.error.message });
        return;
      }
    }
    const nextUrl = path ? supabase.storage.from('team-icons').getPublicUrl(path).data.publicUrl : null;
    const update = await supabase.from('teams').update({ team_icon_url: nextUrl, updated_by: auditUserId }).eq('id', selectedTeamId).select('id').single();
    if (update.error) {
      if (path) await supabase.storage.from('team-icons').remove([path]);
      setSnack({ severity: 'error', message: update.error.message });
    } else {
      const previousPath = teamIconPath(selectedTeam?.team_icon_url);
      if (previousPath) await supabase.storage.from('team-icons').remove([previousPath]);
      await loadTeams();
      setSnack({ severity: 'success', message: file ? 'Team icon saved.' : 'Team icon removed.' });
    }
    setTeamIconSaving(false);
  };

  const unsetOtherPrimaryCaptains = async (assignmentId?: string) => {
    let query = supabase
      .from('team_captain_assignments')
      .update({ is_primary_captain: false, updated_by: auditUserId })
      .eq('team_id', selectedTeamId)
      .eq('is_primary_captain', true)
      .eq('is_active', true)
      .is('deleted_at', null);
    if (assignmentId) query = query.neq('id', assignmentId);
    return query;
  };

  const openAddCaptain = () => {
    if (readOnly) return;
    setEditingAssignment(null);
    setCaptainForm({
      app_user_id: availableUsers[0]?.id || '',
      assignment_role: 'captain',
      is_primary_captain: false,
      is_active: true,
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEditCaptain = (assignment: CaptainAssignment) => {
    if (readOnly) return;
    setEditingAssignment(assignment);
    setCaptainForm({
      app_user_id: assignment.app_user_id,
      assignment_role: assignment.assignment_role,
      is_primary_captain: assignment.is_primary_captain,
      is_active: assignment.is_active,
      notes: assignment.notes || '',
    });
    setDialogOpen(true);
  };

  const saveCaptain = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (!selectedTeamId) return;
    if (!captainForm.app_user_id) {
      setSnack({ severity: 'error', message: 'User is required.' });
      return;
    }

    const primaryCaptain = primaryCaptainApplies(captainForm.assignment_role) && captainForm.is_primary_captain && captainForm.is_active;
    if (primaryCaptain) {
      const { error } = await unsetOtherPrimaryCaptains(editingAssignment?.id);
      if (error) {
        setSnack({ severity: 'error', message: error.message });
        return;
      }
    }

    const payload = {
      team_id: selectedTeamId,
      app_user_id: captainForm.app_user_id,
      assignment_role: captainForm.assignment_role,
      is_primary_captain: primaryCaptain,
      is_active: captainForm.is_active,
      notes: captainForm.notes || null,
      updated_by: auditUserId,
    };
    const permissionDefaults = matrixToAssignmentPatch(
      legacyAssignmentMatrix({ assignment_role: captainForm.assignment_role }),
      detectPermissionPreset(legacyAssignmentMatrix({ assignment_role: captainForm.assignment_role })),
    );
    const result = editingAssignment
      ? await supabase.from('team_captain_assignments').update(payload).eq('id', editingAssignment.id)
      : await supabase.from('team_captain_assignments').insert({ ...payload, ...permissionDefaults, created_by: auditUserId });
    if (result.error) {
      setSnack({ severity: 'error', message: captainSaveErrorMessage(result.error.message) });
      return;
    }
    setDialogOpen(false);
    setSnack({ severity: 'success', message: 'Team access saved.' });
    await loadCaptains();
    await loadTeams();
  };

  const deactivateCaptain = async (assignment: CaptainAssignment) => {
    if (readOnly) return;
    if (!window.confirm(`Remove ${userLabel(users, assignment.app_user_id)} from this team?`)) return;
    const { error } = await supabase
      .from('team_captain_assignments')
      .update({
        is_active: false,
        is_primary_captain: false,
        deleted_at: new Date().toISOString(),
        delete_reason: 'Removed from Team Access section',
        updated_by: auditUserId,
      })
      .eq('id', assignment.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Team access removed.' });
    await loadCaptains();
    await loadTeams();
  };

  const setPrimaryCaptain = async (assignment: CaptainAssignment) => {
    if (readOnly) return;
    const unsetResult = await unsetOtherPrimaryCaptains(assignment.id);
    if (unsetResult.error) {
      setSnack({ severity: 'error', message: unsetResult.error.message });
      return;
    }
    const { error } = await supabase
      .from('team_captain_assignments')
      .update({ is_primary_captain: true, is_active: true, updated_by: auditUserId })
      .eq('id', assignment.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Primary captain updated.' });
    await loadCaptains();
  };

  const saveTcdIntegration = async () => {
    if (!selectedTeamId || readOnly) return;
    setTcdSaving(true);
    const response = await fetch('/api/tcd/integration/save', {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify({
        teamId: selectedTeamId,
        teamUrl: tcdForm.teamUrl,
        username: tcdForm.username,
        password: tcdForm.password,
        enabled: tcdForm.enabled,
      }),
    });
    const result = await response.json() as TcdIntegrationStatus & { error?: string };
    setTcdSaving(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to save TCD integration.' });
      return;
    }
    setTcdStatus(result);
    setTcdForm({ teamUrl: result.teamUrl || '', username: '', password: '', enabled: Boolean(result.enabled) });
    setSnack({ severity: 'success', message: 'TCD integration saved.' });
  };

  const testTcdIntegration = async () => {
    if (!selectedTeamId || readOnly) return;
    setTcdTesting(true);
    setTcdDiagnostic(null);
    const response = await fetch('/api/tcd/integration/test', {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify({ teamId: selectedTeamId }),
    });
    const result = await response.json() as TcdDiagnosticResult;
    setTcdTesting(false);
    setTcdDiagnostic(result);
    await loadTcdStatus();
    if (!response.ok || result.success === false) {
      setSnack({ severity: 'error', message: result.error || result.message || 'Unable to test TCD integration.' });
      return;
    }
    setSnack({ severity: 'success', message: 'TCD connection test completed.' });
  };

  const saveGroupMeIntegration = async () => {
    if (!selectedTeamId || readOnly) return;
    setGroupMeSaving(true);
    const response = await fetch('/api/groupme/integration/save', {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify({
        teamId: selectedTeamId,
        accessToken: groupMeForm.accessToken,
        enabled: groupMeForm.enabled,
      }),
    });
    const result = await response.json() as GroupMeIntegrationStatus & { error?: string };
    setGroupMeSaving(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to save GroupMe integration.' });
      return;
    }
    setGroupMeStatus(result);
    setGroupMeForm({ accessToken: '', enabled: Boolean(result.enabled) });
    setSnack({ severity: 'success', message: 'GroupMe sync settings saved.' });
    await loadGroupMeGroups();
  };

  const testGroupMeIntegration = async () => {
    if (!selectedTeamId || readOnly) return;
    setGroupMeTesting(true);
    const response = await fetch('/api/groupme/integration/test', {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify({ teamId: selectedTeamId }),
    });
    const result = await response.json() as { success?: boolean; message?: string; error?: string };
    setGroupMeTesting(false);
    await loadGroupMeStatus();
    if (!response.ok || result.success === false) {
      setSnack({ severity: 'error', message: result.error || result.message || 'Unable to test GroupMe integration.' });
      return;
    }
    setSnack({ severity: 'success', message: result.message || 'GroupMe connection test completed.' });
  };

  const refreshTeamUpdatesBot = async () => {
    if (!selectedTeamId || readOnly) return;
    setTeamUpdatesSaving(true);
    const response = await fetch('/api/groupme/team-updates/refresh-bot', {
      method: 'POST', headers: await authHeaders(true), body: JSON.stringify({ teamId: selectedTeamId }),
    });
    const result = await response.json() as { settings?: GroupMeTeamUpdatesSettings; bots?: GroupMeBotOption[]; message?: string; error?: string };
    setTeamUpdatesSaving(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to refresh the GroupMe bot.' });
      return;
    }
    setTeamUpdatesSettings(result.settings || null);
    setTeamUpdatesBots(result.bots || []);
    setSnack({ severity: 'success', message: result.message || 'GroupMe bot refreshed.' });
  };

  const saveTeamUpdates = async () => {
    if (!selectedTeamId || !teamUpdatesSettings || readOnly) return;
    setTeamUpdatesSaving(true);
    const response = await fetch('/api/groupme/team-updates/save', {
      method: 'POST', headers: await authHeaders(true), body: JSON.stringify({ teamId: selectedTeamId, ...teamUpdatesSettings }),
    });
    const result = await response.json() as { settings?: GroupMeTeamUpdatesSettings; error?: string };
    setTeamUpdatesSaving(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to save GroupMe Team Updates.' });
      return;
    }
    setTeamUpdatesSettings(result.settings || null);
    setSnack({ severity: 'success', message: 'GroupMe Team Updates saved.' });
  };

  const previewTeamUpdate = async (updateType: 'upcoming_schedule' | 'upcoming_lineup', matchId?: string) => {
    if (!selectedTeamId) return;
    const response = await fetch('/api/groupme/team-updates/preview', {
      method: 'POST', headers: await authHeaders(true), body: JSON.stringify({ teamId: selectedTeamId, updateType, matchId }),
    });
    const result = await response.json() as GroupMeTeamUpdatePreview & { error?: string };
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to prepare GroupMe preview.' });
      return;
    }
    setTeamUpdatesPreview({ ...result, matchId });
  };

  const sendTeamUpdate = async (confirmUnchanged = false) => {
    if (!selectedTeamId || !teamUpdatesPreview) return;
    const updateType = teamUpdatesPreview.type;
    setTeamUpdatesSaving(true);
    const response = await fetch('/api/groupme/team-updates/send', {
      method: 'POST', headers: await authHeaders(true), body: JSON.stringify({
        teamId: selectedTeamId, updateType, matchId: teamUpdatesPreview.type === 'upcoming_lineup' ? (teamUpdatesPreview as GroupMeTeamUpdatePreview & { matchId?: string }).matchId : undefined, confirmUnchanged,
      }),
    });
    const result = await response.json() as { confirmationRequired?: boolean; warning?: string; error?: string; message?: string };
    setTeamUpdatesSaving(false);
    if (result.confirmationRequired) {
      setTeamUpdatesPreview((current) => current ? { ...current, warning: result.warning } : current);
      return;
    }
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to send GroupMe update.' });
      return;
    }
    setTeamUpdatesPreview(null);
    setSnack({ severity: 'success', message: result.message || 'GroupMe update sent.' });
    void loadTeamUpdates();
  };

  const teamFieldByName = useMemo(() => new Map(teamEntity.fields.map((field) => [field.name, field])), [teamEntity.fields]);
  const organizationLabel = lookupLabel(lookups, 'organizations', teamForm.organization_id).toLowerCase();

  const shouldShowTeamField = (fieldName: string): boolean => {
    const value = teamForm[fieldName];
    if (fieldName === 'team_flight') return organizationLabel.includes('tcd') || Boolean(value);
    if (fieldName === 'usta_team_number') return organizationLabel.includes('usta') || Boolean(value);
    return true;
  };

  const fieldColumnSpan = (field: FieldConfig) => {
    if (field.type === 'textarea' || field.type === 'url' || field.name.includes('url') || field.name.includes('template') || field.name === 'notes') return '1 / -1';
    if (['team_color', 'team_gender_type', 'player_rating_basis', 'default_warmup_minutes', 'default_target_match_count_per_player', 'team_flight', 'player_portal_access_enabled', 'player_event_self_service_enabled', 'player_portal_tentative_lineups_enabled', 'player_portal_tentative_lineup_lookahead_matches', 'groupme_event_reminders_enabled', 'is_active'].includes(field.name)) return { xs: '1 / -1', md: 'span 3' };
    return { xs: '1 / -1', md: 'span 6' };
  };

  const renderTeamField = (field: FieldConfig) => {
    const value = teamForm[field.name] ?? '';
    const setField = (nextValue: string | number | boolean) => setTeamForm((current) => ({ ...current, [field.name]: nextValue }));
    const gridColumn = fieldColumnSpan(field);
    const recommendationSettingDisabled = (field.name === 'target_match_variance' && teamForm.enforce_target_matches !== true)
      || (field.name === 'max_weeks_without_match' && teamForm.enforce_max_sit !== true)
      || (field.name === 'player_portal_tentative_lineup_lookahead_matches' && teamForm.player_portal_tentative_lineups_enabled !== true);
    if (field.name === 'groupme_group_name') return null;
    if (field.name === 'default_groupme_event_reminders') {
      const reminders = normalizeGroupMeReminders(value);
      const setReminder = (index: number, nextValue: string) => {
        const next = [...reminders];
        if (nextValue === '') next.splice(index, 1);
        else next[index] = Number(nextValue);
        setTeamForm((current) => ({ ...current, default_groupme_event_reminders: normalizeGroupMeReminders(next) }));
      };
      return (
        <Box key={field.name} sx={{ gridColumn: '1 / -1' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {field.label}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            {[0, 1].map((index) => (
              <FormControl key={index} fullWidth>
                <InputLabel>{index === 0 ? 'First Reminder' : 'Second Reminder'}</InputLabel>
                <Select
                  label={index === 0 ? 'First Reminder' : 'Second Reminder'}
                  value={reminders[index] == null ? '' : String(reminders[index])}
                  onChange={(event) => setReminder(index, event.target.value)}
                  disabled={readOnly}
                >
                  <MenuItem value="">No reminder</MenuItem>
                  {groupMeReminderOptions.map((option) => (
                    <MenuItem key={option.value} value={String(option.value)} disabled={reminders.includes(option.value) && reminders[index] !== option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        </Box>
      );
    }
    if (field.name === 'groupme_group_id') {
      const selectedGroupId = String(value || '');
      const selectedGroup = groupMeGroups.find((group) => group.id === selectedGroupId);
      const selectedIsUnavailable = selectedGroup && !selectedGroup.available;
      return (
        <FormControl key={field.name} fullWidth sx={{ gridColumn: '1 / -1' }}>
          <InputLabel>GroupMe Group</InputLabel>
          <Select
            label="GroupMe Group"
            value={selectedGroupId}
            onChange={(event) => {
              const nextGroupId = event.target.value;
              const nextGroup = groupMeGroups.find((group) => group.id === nextGroupId);
              setTeamForm((current) => ({
                ...current,
                groupme_group_id: nextGroupId || null,
                groupme_group_name: nextGroup?.name || null,
              }));
            }}
            disabled={readOnly || groupMeGroupsLoading || (!groupMeStatus?.tokenConfigured && !groupMeStatus?.fallbackAvailable)}
          >
            <MenuItem value="">No GroupMe group</MenuItem>
            {groupMeGroups.map((group) => (
              <MenuItem key={group.id} value={group.id} disabled={!group.available}>
                {group.name}{!group.available && group.mappedTeamName ? ` (mapped to ${group.mappedTeamName})` : ''}
              </MenuItem>
            ))}
            {selectedGroupId && !selectedGroup && (
              <MenuItem value={selectedGroupId}>{teamForm.groupme_group_name || selectedGroupId}</MenuItem>
            )}
          </Select>
          <Typography variant="caption" color={selectedIsUnavailable ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5 }}>
            {groupMeGroupsLoading
              ? 'Loading GroupMe groups...'
              : (!groupMeStatus?.tokenConfigured && !groupMeStatus?.fallbackAvailable)
                ? 'Save your GroupMe token below before selecting a group.'
                : selectedIsUnavailable
                  ? 'This GroupMe group is already mapped to another active team.'
                  : 'Each GroupMe group can be mapped to only one active CourtCaptain team.'}
          </Typography>
        </FormControl>
      );
    }
    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          key={field.name}
          control={<Checkbox checked={Boolean(value)} onChange={(event) => setField(event.target.checked)} disabled={readOnly} />}
          label={field.label}
          sx={{ gridColumn }}
        />
      );
    }
    if (field.name === 'team_color') {
      const selectedColor = String(value || 'gray') as TeamColor;
      const selectedColorLabel = teamColorOptions.find((option) => option.value === selectedColor)?.label || 'Gray';
      return (
        <FormControl key={field.name} required={field.required} sx={{ gridColumn }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75 }}>
            {field.label}
          </Typography>
          <Stack spacing={0.75} alignItems="flex-start">
            <Button
              type="button"
              variant="outlined"
              disabled={readOnly}
              aria-expanded={teamColorPickerOpen}
              onClick={() => setTeamColorPickerOpen((current) => !current)}
              sx={{ minHeight: 40, px: 1.25, justifyContent: 'flex-start' }}
            >
              <Box component="span" sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: teamAccentColor(selectedColor), mr: 1, border: '1px solid rgba(0,0,0,0.22)' }} />
              {selectedColorLabel}
            </Button>
            {teamColorPickerOpen && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {teamColorOptions.map((option) => {
                  const optionColor = option.value as TeamColor;
                  const selected = selectedColor === optionColor;
                  return (
                    <Button
                      key={option.value}
                      type="button"
                      variant="outlined"
                      aria-label={`Select ${option.label} team color`}
                      aria-pressed={selected}
                      disabled={readOnly}
                      onClick={() => {
                        setField(option.value);
                        setTeamColorPickerOpen(false);
                      }}
                      sx={{
                        minWidth: 34,
                        width: 34,
                        height: 34,
                        p: 0,
                        borderRadius: '50%',
                        borderWidth: selected ? 3 : 1,
                        borderColor: selected ? 'text.primary' : 'divider',
                        bgcolor: teamAccentColor(optionColor),
                        color: '#fff',
                        boxShadow: selected ? 2 : 0,
                        '&:hover': {
                          bgcolor: teamAccentColor(optionColor),
                          borderColor: 'text.primary',
                        },
                      }}
                    >
                      {selected ? '✓' : ''}
                    </Button>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </FormControl>
      );
    }
    if (field.type === 'select') {
      const options = field.name === 'league_configuration_id'
        ? lookups.league_configurations.filter((option) => option.workspace_id === teamForm.workspace_id && (option.is_active !== false || option.value === value))
        : field.options || (field.optionsKey ? lookups[field.optionsKey] : []);
      return (
        <FormControl key={field.name} fullWidth required={field.required} sx={{ gridColumn }}>
          <InputLabel>{field.label}</InputLabel>
          <Select label={field.label} value={value === null || value === undefined ? '' : String(value)} onChange={(event) => setField(event.target.value)} disabled={readOnly || recommendationSettingDisabled}>
            {field.name !== 'target_match_variance' && <MenuItem value="">None</MenuItem>}
            {options.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        key={field.name}
        label={field.label}
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        value={value}
        onChange={(event) => setField(field.type === 'number' ? event.target.value : event.target.value)}
        required={field.required}
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 3 : undefined}
        helperText={field.helperText}
        InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
        inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
        disabled={readOnly || recommendationSettingDisabled}
        sx={{ gridColumn }}
        fullWidth
      />
    );
  };

  const renderTcdDiagnosticPreview = () => {
    const matches = tcdDiagnostic?.parsed?.matches || [];
    const players = tcdDiagnostic?.parsed?.players || [];
    const previewMatches = matches.slice(0, 5);
    const previewPlayers = players.slice(0, 8);
    const availabilityCandidates = tcdDiagnostic?.navigation?.availabilityCandidates || [];
    if (!tcdDiagnostic) return null;
    return (
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>TCD Connection</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
            {[
              ['Authentication', tcdDiagnostic.diagnostics?.authenticationSuccessful ? 'Successful' : 'Not confirmed'],
              ['Team Overview', tcdDiagnostic.diagnostics?.teamOverviewRetrieved ? 'Retrieved' : 'Not retrieved'],
              ['Roster / Availability', tcdDiagnostic.diagnostics?.rosterAvailabilityRetrieved ? 'Retrieved' : 'Not retrieved'],
              ['Players', String(players.length || 0)],
              ['Events', String(matches.length || 0)],
            ].map(([label, value]) => (
              <Paper key={label} variant="outlined" sx={{ p: 1 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{value}</Typography>
              </Paper>
            ))}
          </Box>
          {tcdDiagnostic.diagnostics?.rosterAvailabilityMechanism && (
            <Typography variant="body2" color="text.secondary">
              Roster / Availability mechanism: {tcdDiagnostic.diagnostics.rosterAvailabilityMechanism}
              {tcdDiagnostic.diagnostics.editMyAvailabilityFound ? ' • Edit My Availability was also detected and ignored.' : ''}
            </Typography>
          )}
          {availabilityCandidates.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Candidate Tenniscores links: {availabilityCandidates.slice(0, 4).map((candidate) => candidate.text || candidate.href || 'Unnamed link').join(' · ')}
            </Typography>
          )}
          {tcdDiagnostic.comparison?.players && (
            <Typography variant="body2">
              Player matching: {tcdDiagnostic.comparison.players.exact} exact · {tcdDiagnostic.comparison.players.possible} possible · {tcdDiagnostic.comparison.players.unmatched} unmatched
            </Typography>
          )}
          {tcdDiagnostic.comparison?.matches && (
            <Typography variant="body2">
              Match matching: {tcdDiagnostic.comparison.matches.matched} matched · {tcdDiagnostic.comparison.matches.possible} possible · {tcdDiagnostic.comparison.matches.unmatched} unmatched
            </Typography>
          )}
          {previewMatches.length > 0 && previewPlayers.length > 0 && (
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Player</TableCell>
                    {previewMatches.map((match, index) => (
                      <TableCell key={`${match.date || 'event'}-${index}`} sx={{ fontWeight: 800 }}>{match.date || match.rawHeader || `Event ${index + 1}`}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewPlayers.map((player) => (
                    <TableRow key={player.name}>
                      <TableCell>{player.name}</TableCell>
                      {previewMatches.map((match, index) => {
                        const row = match.players.find((candidate) => normalizeName(candidate.name) === normalizeName(player.name));
                        return <TableCell key={`${player.name}-${index}`}>{row?.availability || '-'}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderTcdIntegrationSection = () => (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>TCD / Tenniscores Availability</Typography>
            <Typography variant="body2" color="text.secondary">
              Proof-of-concept diagnostic for reading team-wide Roster / Availability without updating CourtCaptain availability.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={tcdStatus?.credentialsConfigured ? 'Credentials configured' : 'Credentials not configured'} color={tcdStatus?.credentialsConfigured ? 'success' : 'default'} />
            <Chip size="small" label={tcdStatus?.enabled ? 'Enabled' : 'Disabled'} color={tcdStatus?.enabled ? 'success' : 'default'} />
          </Stack>
        </Box>
        {tcdLoading && <LinearProgress />}
        {tcdStatus?.serverConfigured === false && (
          <Alert severity="warning">TCD integration is missing staging secret: {tcdStatus.missingConfigKeys?.join(', ')}.</Alert>
        )}
        {tcdStatus?.supabaseProjectRef && (
          <Alert severity={import.meta.env.COURTCAPTAIN_ENV === 'staging' ? 'info' : 'warning'}>
            TCD integration is using Supabase project {tcdStatus.supabaseProjectRef}.
          </Alert>
        )}
        {tcdStatus?.lastError && <Alert severity="warning">{tcdStatus.lastError}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 1.5 }}>
          <TextField
            label="TCD / Tenniscores Team URL"
            value={tcdForm.teamUrl}
            onChange={(event) => setTcdForm((current) => ({ ...current, teamUrl: event.target.value }))}
            disabled={readOnly || tcdSaving || tcdTesting}
            sx={{ gridColumn: '1 / -1' }}
            fullWidth
          />
          <TextField
            label="Username"
            value={tcdForm.username}
            onChange={(event) => setTcdForm((current) => ({ ...current, username: event.target.value }))}
            disabled={readOnly || tcdSaving || tcdTesting}
            helperText={tcdStatus?.credentialsConfigured ? 'Leave blank to keep saved username.' : undefined}
            sx={{ gridColumn: { xs: '1 / -1', md: 'span 5' } }}
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={tcdForm.password}
            onChange={(event) => setTcdForm((current) => ({ ...current, password: event.target.value }))}
            disabled={readOnly || tcdSaving || tcdTesting}
            helperText={tcdStatus?.credentialsConfigured ? 'Leave blank to keep saved password.' : undefined}
            sx={{ gridColumn: { xs: '1 / -1', md: 'span 5' } }}
            fullWidth
          />
          <FormControlLabel
            control={<Checkbox checked={tcdForm.enabled} onChange={(event) => setTcdForm((current) => ({ ...current, enabled: event.target.checked }))} disabled={readOnly || tcdSaving || tcdTesting} />}
            label="Enabled"
            sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Last tested: {formatDateTime(tcdStatus?.lastTestedAt)} · Last success: {formatDateTime(tcdStatus?.lastSuccessAt)}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
            <Button size="small" variant="outlined" disabled={readOnly || tcdSaving || tcdTesting || !selectedTeamId} onClick={() => void saveTcdIntegration()}>
              Save
            </Button>
            <Button size="small" variant="contained" disabled={readOnly || tcdSaving || tcdTesting || !selectedTeamId || !tcdStatus?.configured || !tcdStatus.enabled} onClick={() => void testTcdIntegration()}>
              {tcdTesting ? 'Testing...' : 'Test Connection'}
            </Button>
          </Stack>
        </Stack>
        {renderTcdDiagnosticPreview()}
      </Stack>
    </Paper>
  );

  const renderGroupMeIntegrationSection = () => (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>GroupMe Event Sync</Typography>
            <Typography variant="body2" color="text.secondary">
              Save your GroupMe access token once, then enable event sync for this team.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={groupMeStatus?.tokenConfigured ? 'User token configured' : 'User token not configured'} color={groupMeStatus?.tokenConfigured ? 'success' : 'default'} />
            <Chip size="small" label={groupMeStatus?.enabled ? 'Team sync enabled' : 'Team sync disabled'} color={groupMeStatus?.enabled ? 'success' : 'default'} />
          </Stack>
        </Box>
        {groupMeLoading && <LinearProgress />}
        {groupMeStatus?.serverConfigured === false && (
          <Alert severity="warning">GroupMe integration is missing staging secret: {groupMeStatus.missingConfigKeys?.join(', ')}.</Alert>
        )}
        {groupMeStatus?.fallbackAvailable && !groupMeStatus.tokenConfigured && (
          <Alert severity="info">This team can still use the legacy staging GroupMe token until a user token is saved.</Alert>
        )}
        {!teamForm.groupme_group_id && (
          <Alert severity="warning">Select a GroupMe group in this team&apos;s Communication section before enabling sync.</Alert>
        )}
        {groupMeStatus?.lastError && <Alert severity="warning">{groupMeStatus.lastError}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 1.5 }}>
          <Alert severity={groupMeStatus?.tokenConfigured || groupMeStatus?.fallbackAvailable ? 'info' : 'warning'} sx={{ gridColumn: { xs: '1 / -1', md: 'span 9' } }}>
            Manage your personal GroupMe access token in GroupMe RSVP Sync. This team only needs the GroupMe group selection above and this sync checkbox.
          </Alert>
          <FormControlLabel
            control={<Checkbox checked={groupMeForm.enabled} onChange={(event) => setGroupMeForm((current) => ({ ...current, enabled: event.target.checked }))} disabled={readOnly || groupMeSaving || groupMeTesting || !teamForm.groupme_group_id} />}
            label="Use GroupMe RSVP sync"
            sx={{ gridColumn: { xs: '1 / -1', md: 'span 3' } }}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Last tested: {formatDateTime(groupMeStatus?.lastTestedAt)} · Last sync: {formatDateTime(groupMeStatus?.lastSyncedAt || groupMeStatus?.lastSuccessAt)}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
            <Button size="small" variant="outlined" disabled={readOnly || groupMeSaving || groupMeTesting || !selectedTeamId} onClick={() => void saveGroupMeIntegration()}>
              Save
            </Button>
            <Button size="small" variant="contained" disabled={readOnly || groupMeSaving || groupMeTesting || !selectedTeamId || (!groupMeStatus?.tokenConfigured && !groupMeStatus?.fallbackAvailable)} onClick={() => void testGroupMeIntegration()}>
              {groupMeTesting ? 'Testing...' : 'Test Connection'}
            </Button>
          </Stack>
        </Stack>
        {teamForm.groupme_group_id && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>GroupMe Bot</Typography>
                <Typography variant="caption" color="text.secondary">Connect the bot used for team communications. Schedule and lineup publishing settings are managed in Communications.</Typography>
              </Box>
              {teamUpdatesSettings?.botValidationError && <Alert severity="warning">{teamUpdatesSettings.botValidationError}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>GroupMe Bot</InputLabel>
                  <Select
                    label="GroupMe Bot"
                    value={teamUpdatesSettings?.botId || ''}
                    disabled={readOnly || teamUpdatesSaving || teamUpdatesBots.length === 0}
                    onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, botId: String(event.target.value) || null } : current)}
                  >
                    <MenuItem value=""><em>Choose after refreshing bots</em></MenuItem>
                    {teamUpdatesBots.map((bot) => <MenuItem key={bot.id} value={bot.id}>{bot.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="outlined" disabled={readOnly || teamUpdatesSaving} onClick={() => void refreshTeamUpdatesBot()}>{teamUpdatesSaving ? 'Refreshing…' : 'Refresh Bots'}</Button>
                <Button variant="contained" disabled={readOnly || teamUpdatesSaving || !teamUpdatesSettings?.botId} onClick={() => void saveTeamUpdates()}>{teamUpdatesSaving ? 'Saving…' : 'Save Bot'}</Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Paper>
  );

  const renderGroupMeTeamUpdatesSection = () => {
    const settings = teamUpdatesSettings;
    const botConnected = settings?.botStatus === 'connected';
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>GroupMe Communications</Typography>
            <Typography variant="body2" color="text.secondary">
              Team Updates use this team&apos;s GroupMe bot only. They do not depend on GroupMe Event Sync or post as a captain.
            </Typography>
          </Box>
          {teamUpdatesLoading && <LinearProgress />}
          {!teamForm.groupme_group_id ? (
            <Alert severity="warning">Link a GroupMe group in the Communication section above before configuring Team Updates.</Alert>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Linked Group</Typography>
                  <Typography variant="body2" color="text.secondary">{teamForm.groupme_group_name || teamForm.groupme_group_id}</Typography>
                </Box>
                <Chip size="small" color={botConnected ? 'success' : settings?.botStatus === 'multiple' ? 'warning' : 'default'} label={botConnected ? `${settings?.botName || 'GroupMe bot'} — Connected` : settings?.botStatus === 'multiple' ? 'Choose a GroupMe bot' : 'No validated GroupMe bot'} />
              </Stack>
              {settings?.botValidationError && <Alert severity="warning">{settings.botValidationError}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>GroupMe Bot</InputLabel>
                  <Select label="GroupMe Bot" value={settings?.botId || ''} disabled={readOnly || teamUpdatesSaving || teamUpdatesBots.length === 0} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, botId: String(event.target.value) || null } : current)}>
                    <MenuItem value=""><em>Choose after refreshing bots</em></MenuItem>
                    {teamUpdatesBots.map((bot) => <MenuItem key={bot.id} value={bot.id}>{bot.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="outlined" disabled={readOnly || teamUpdatesSaving} onClick={() => void refreshTeamUpdatesBot()}>{teamUpdatesSaving ? 'Refreshing...' : 'Refresh Bot'}</Button>
              </Stack>

              <Divider />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>GroupMe Team Updates</Typography>
                  <Typography variant="caption" color="text.secondary">Save changes before scheduled updates use the new settings.</Typography>
                </Box>
                <Button
                  variant="contained"
                  disabled={readOnly || teamUpdatesSaving || !settings}
                  onClick={() => void saveTeamUpdates()}
                >
                  {teamUpdatesSaving ? 'Saving…' : 'Save Team Update Settings'}
                </Button>
              </Stack>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                    <Box><Typography variant="body2" sx={{ fontWeight: 800 }}>Upcoming Schedule</Typography><Typography variant="caption" color="text.secondary">Recurring player-visible events posted from the team bot.</Typography></Box>
                    <Button variant="outlined" size="small" disabled={!botConnected || teamUpdatesSaving} onClick={() => void previewTeamUpdate('upcoming_schedule')}>Send Schedule Now</Button>
                  </Stack>
                  <FormControlLabel control={<Checkbox checked={Boolean(settings?.scheduleEnabled)} disabled={readOnly || !settings} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, scheduleEnabled: event.target.checked } : current)} />} label="Scheduled" />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                    <FormControl size="small"><InputLabel>Day</InputLabel><Select label="Day" value={settings?.scheduleDayOfWeek ?? 0} disabled={readOnly || !settings} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, scheduleDayOfWeek: Number(event.target.value) } : current)}>{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => <MenuItem key={day} value={index}>{day}</MenuItem>)}</Select></FormControl>
                    <TextField size="small" label="Time" type="time" value={settings?.scheduleTime || '18:00'} disabled={readOnly || !settings} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, scheduleTime: event.target.value } : current)} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="Look Ahead Days" type="number" value={settings?.scheduleLookaheadDays || 14} disabled={readOnly || !settings} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, scheduleLookaheadDays: Number(event.target.value) } : current)} />
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Box><Typography variant="body2" sx={{ fontWeight: 800 }}>Upcoming Lineups</Typography><Typography variant="caption" color="text.secondary">Only Published lineups can be shared.</Typography></Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                    <FormControl size="small"><InputLabel>Mode</InputLabel><Select label="Mode" value={settings?.lineupMode || 'disabled'} disabled={readOnly || !settings} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, lineupMode: event.target.value as GroupMeTeamUpdatesSettings['lineupMode'] } : current)}><MenuItem value="disabled">Disabled</MenuItem><MenuItem value="manual_only">Manual Only</MenuItem><MenuItem value="scheduled">Scheduled</MenuItem></Select></FormControl>
                    <TextField size="small" label="Look Ahead Days" type="number" value={settings?.lineupLookaheadDays || 7} disabled={readOnly || !settings || settings.lineupMode !== 'scheduled'} onChange={(event) => setTeamUpdatesSettings((current) => current ? { ...current, lineupLookaheadDays: Number(event.target.value) } : current)} />
                    <FormControl size="small"><InputLabel>Eligible Match</InputLabel><Select label="Eligible Match" value={teamUpdatesLineupMatchId} disabled={!botConnected || settings?.lineupMode === 'disabled'} onChange={(event) => setTeamUpdatesLineupMatchId(String(event.target.value))}><MenuItem value=""><em>Choose match</em></MenuItem>{teamUpdatesLineupMatches.map((match) => <MenuItem key={match.id} value={match.id}>{match.match_date} · vs {match.opponent_name || 'TBD'} · {match.lineup_status}</MenuItem>)}</Select></FormControl>
                  </Box>
                  <Button size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={!botConnected || settings?.lineupMode === 'disabled' || !teamUpdatesLineupMatchId} onClick={() => void previewTeamUpdate('upcoming_lineup', teamUpdatesLineupMatchId)}>Publish Lineup to GroupMe</Button>
                </Stack>
              </Paper>
              <Typography variant="caption" color="text.secondary">Timezone: {settings?.timezone || 'America/Chicago'}. Scheduled lineup evaluation runs with the existing GroupMe schedule job and skips unchanged lineup content.</Typography>
            </>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderOverview = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Overview</Typography>
            <Typography variant="body2" color="text.secondary">
              Team-specific configuration, league details, home facility, defaults, links, and notes.
            </Typography>
          </Box>
          {!readOnly && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
              sx={{
                color: teamValidationError ? 'error.main' : teamFormDirty ? 'warning.main' : 'success.main',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1.2, textAlign: { xs: 'left', sm: 'right' } }}>
                {teamValidationError || (teamFormDirty ? 'Unsaved changes' : 'No unsaved changes')}
              </Typography>
              <Stack direction="row" spacing={0.75} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                <Button size="small" variant="outlined" disabled={!teamFormDirty} onClick={discardTeamChanges}>Discard Changes</Button>
                <Button size="small" variant="contained" disabled={!teamFormDirty || Boolean(teamValidationError)} onClick={() => void saveTeam()}>{selectedTeamId ? 'Save Team' : 'Create Team'}</Button>
              </Stack>
            </Stack>
          )}
        </Box>
        {overviewSections.map((section) => {
          const fields = section.fields
            .map((fieldName) => teamFieldByName.get(fieldName))
            .filter((field): field is FieldConfig => Boolean(field && shouldShowTeamField(field.name)));
          if (fields.length === 0) return null;
          return (
            <Paper key={section.title} variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{section.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{section.description}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 2 }}>
                {fields.map(renderTeamField)}
              </Box>
            </Paper>
          );
        })}
        {selectedTeam && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Team Appearance</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>The team icon is separate from workspace branding. Team color is set above.</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <TeamIcon name={selectedTeam.short_name || selectedTeam.team_name} color={String(selectedTeam.team_color || 'gray')} url={selectedTeam.team_icon_url} />
              <Button component="label" variant="outlined" disabled={readOnly || teamIconSaving}>
                {selectedTeam.team_icon_url ? 'Replace Team Icon' : 'Upload Team Icon'}
                <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  if (file) void saveTeamIcon(file);
                  event.currentTarget.value = '';
                }} />
              </Button>
              {selectedTeam.team_icon_url && <Button color="error" variant="text" disabled={readOnly || teamIconSaving} onClick={() => void saveTeamIcon(null)}>Remove Icon</Button>}
            </Stack>
          </Paper>
        )}
        {selectedTeamId && renderGroupMeIntegrationSection()}
        {selectedTeamId && renderTcdIntegrationSection()}
      </Stack>
    </Paper>
  );

  const renderLineupRecommendations = () => {
    const participationFields = ['enforce_target_matches', 'target_match_variance', 'enforce_max_sit', 'max_weeks_without_match']
      .map((fieldName) => teamFieldByName.get(fieldName))
      .filter((field): field is FieldConfig => Boolean(field));
    const partnershipFields = ['use_partner_compatibility', 'use_partner_scheduling_preferences']
      .map((fieldName) => teamFieldByName.get(fieldName))
      .filter((field): field is FieldConfig => Boolean(field));
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Lineup Recommendations</Typography>
              <Typography variant="body2" color="text.secondary">Configure the participation and soft-partnership rules used for this team’s lineups.</Typography>
            </Box>
            {!readOnly && (
              <Stack direction="row" spacing={0.75} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                <Button size="small" variant="outlined" disabled={!teamFormDirty} onClick={discardTeamChanges}>Discard Changes</Button>
                <Button size="small" variant="contained" disabled={!teamFormDirty || Boolean(teamValidationError)} onClick={() => void saveTeam()}>Save Changes</Button>
              </Stack>
            )}
          </Box>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Participation</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Baseline participation fairness is always active. These settings add stricter season-long and short-term participation constraints.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 2 }}>
              {participationFields.map(renderTeamField)}
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Partnerships</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Hard partner rules such as Only Play Together remain enforced. These toggles control the soft pairing preferences.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 2 }}>
              {partnershipFields.map(renderTeamField)}
            </Box>
          </Paper>
          <Typography variant="caption" color="text.secondary">Player ranking method: {teamForm.player_rating_basis === 'tier' ? 'Tiering' : 'EDR'}.</Typography>
        </Stack>
      </Paper>
    );
  };

  const renderFeesPayments = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Fees &amp; Payments</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the external payment methods your players can use to pay their captain. CourtCaptain does not process these payments.
          </Typography>
        </Box>
        {paymentMethodsLoading && <LinearProgress />}
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Accepted Payment Methods</Typography>
          <Stack spacing={1.25}>
            {paymentMethods.length === 0 && !paymentMethodsLoading && (
              <Typography variant="body2" color="text.secondary">No payment methods are configured for this team.</Typography>
            )}
            {paymentMethods.map((method) => (
              <Stack key={method.id} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    label="Method"
                    value={method.payment_method}
                    disabled={readOnly}
                    onChange={(event) => void updatePaymentMethod(method, { payment_method: event.target.value as TeamPaymentMethod['payment_method'], custom_label: event.target.value === 'Other' ? method.custom_label : null })}
                  >
                    {['Venmo', 'Zelle', 'PayPal', 'Cash App', 'Other'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                  </Select>
                </FormControl>
                {method.payment_method === 'Other' && (
                  <TextField size="small" label="Custom Label" value={method.custom_label || ''} disabled={readOnly} onBlur={(event) => void updatePaymentMethod(method, { custom_label: event.target.value.trim() })} onChange={(event) => setPaymentMethods((current) => current.map((row) => row.id === method.id ? { ...row, custom_label: event.target.value } : row))} />
                )}
                <TextField size="small" label="Identifier" value={method.payment_identifier} disabled={readOnly} fullWidth onBlur={(event) => void updatePaymentMethod(method, { payment_identifier: event.target.value.trim() })} onChange={(event) => setPaymentMethods((current) => current.map((row) => row.id === method.id ? { ...row, payment_identifier: event.target.value } : row))} />
                <TextField size="small" label="Order" type="number" value={method.sort_order} disabled={readOnly} sx={{ width: { xs: '100%', md: 90 } }} inputProps={{ min: 0 }} onBlur={(event) => void updatePaymentMethod(method, { sort_order: Math.max(0, Number(event.target.value) || 0) })} onChange={(event) => setPaymentMethods((current) => current.map((row) => row.id === method.id ? { ...row, sort_order: Math.max(0, Number(event.target.value) || 0) } : row))} />
                <FormControlLabel control={<Checkbox checked={method.is_enabled} disabled={readOnly} onChange={(event) => void updatePaymentMethod(method, { is_enabled: event.target.checked })} />} label="Enabled" sx={{ whiteSpace: 'nowrap' }} />
                <Button color="error" size="small" startIcon={<DeleteIcon />} disabled={readOnly} onClick={() => void deletePaymentMethod(method)}>Delete</Button>
              </Stack>
            ))}
          </Stack>
        </Paper>
        {!readOnly && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Add Payment Method</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Method</InputLabel>
                <Select label="Method" value={paymentMethodForm.payment_method} onChange={(event) => setPaymentMethodForm((current) => ({ ...current, payment_method: event.target.value as TeamPaymentMethod['payment_method'], custom_label: event.target.value === 'Other' ? current.custom_label : '' }))}>
                  {['Venmo', 'Zelle', 'PayPal', 'Cash App', 'Other'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </Select>
              </FormControl>
              {paymentMethodForm.payment_method === 'Other' && <TextField size="small" label="Custom Label" value={paymentMethodForm.custom_label} onChange={(event) => setPaymentMethodForm((current) => ({ ...current, custom_label: event.target.value }))} />}
              <TextField size="small" label="Identifier" value={paymentMethodForm.payment_identifier} fullWidth onChange={(event) => setPaymentMethodForm((current) => ({ ...current, payment_identifier: event.target.value }))} />
              <TextField size="small" label="Order" type="number" value={paymentMethodForm.sort_order} sx={{ width: { xs: '100%', md: 90 } }} inputProps={{ min: 0 }} onChange={(event) => setPaymentMethodForm((current) => ({ ...current, sort_order: Math.max(0, Number(event.target.value) || 0) }))} />
              <FormControlLabel control={<Checkbox checked={paymentMethodForm.is_enabled} onChange={(event) => setPaymentMethodForm((current) => ({ ...current, is_enabled: event.target.checked }))} />} label="Enabled" sx={{ whiteSpace: 'nowrap' }} />
              <Button variant="contained" startIcon={<AddIcon />} disabled={paymentMethodSaving} onClick={() => void addPaymentMethod()}>Add</Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Paper>
  );

  const renderAccess = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Access</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage captain, co-captain, assistant captain, and viewer access for this team.
            </Typography>
          </Box>
          {!readOnly && (
            <Button variant="contained" startIcon={<AddIcon />} disabled={!selectedTeamId} onClick={openAddCaptain}>
              Add Access
            </Button>
          )}
        </Box>
        {captainsLoading && <LinearProgress />}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }, gap: 1.5 }}>
          {[
            ['Total Users', accessSummary.total, `${Math.max(accessSummary.total - accessSummary.active, 0)} inactive`],
            ['Captains', accessSummary.captains, 'Primary owner roles'],
            ['Co-Captains', accessSummary.coCaptains, 'Can manage team'],
            ['Assistant Captains', accessSummary.assistants, 'Support access'],
            ['Viewers', accessSummary.viewers, 'Read-only access'],
            ['Active Users', accessSummary.active, `${accessSummary.total ? Math.round((accessSummary.active / accessSummary.total) * 100) : 0}%`],
          ].map(([label, value, helper]) => (
            <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 800 }}>{value}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
              <Typography variant="caption" color="text.secondary">{helper}</Typography>
            </Paper>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr 1fr auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField size="small" label="Search users" value={accessSearch} onChange={(event) => setAccessSearch(event.target.value)} />
          <FormControl size="small">
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={accessRoleFilter} onChange={(event) => setAccessRoleFilter(event.target.value)}>
              <MenuItem value="all">All Roles</MenuItem>
              {assignmentRoleOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={accessStatusFilter} onChange={(event) => setAccessStatusFilter(event.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All Statuses</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Primary Captain</InputLabel>
            <Select label="Primary Captain" value={accessPrimaryFilter} onChange={(event) => setAccessPrimaryFilter(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="primary">Primary Only</MenuItem>
              <MenuItem value="not_primary">Not Primary</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox checked={showInactiveAccess} onChange={(event) => setShowInactiveAccess(event.target.checked)} />} label="Show inactive users" />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Primary Captain</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Added</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Added By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                {!readOnly && <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCaptainAssignments.map((assignment) => {
                const user = userById.get(assignment.app_user_id);
                const createdBy = assignment.created_by ? userById.get(assignment.created_by) : undefined;
                return (
                <TableRow key={assignment.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{user?.display_name || assignment.app_user_id}</Typography>
                    {user?.email && <Typography variant="caption" color="text.secondary">{user.email}</Typography>}
                  </TableCell>
                  <TableCell>{roleLabel(assignment.assignment_role)}</TableCell>
                  <TableCell>{primaryCaptainApplies(assignment.assignment_role) ? (assignment.is_primary_captain ? 'Yes' : 'No') : '-'}</TableCell>
                  <TableCell><Chip size="small" color={assignment.is_active ? 'success' : 'default'} label={assignment.is_active ? 'Active' : 'Inactive'} /></TableCell>
                  <TableCell>{formatDateTime(assignment.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{createdBy?.display_name || '-'}</Typography>
                    {createdBy?.email && <Typography variant="caption" color="text.secondary">{createdBy.email}</Typography>}
                  </TableCell>
                  <TableCell>{assignment.notes || '-'}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditCaptain(assignment)}>Edit</Button>
                        <Button size="small" startIcon={<StarIcon />} disabled={assignment.is_primary_captain || !assignment.is_active || !primaryCaptainApplies(assignment.assignment_role)} onClick={() => void setPrimaryCaptain(assignment)}>Set Primary</Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => void deactivateCaptain(assignment)}>Remove</Button>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );})}
              {filteredCaptainAssignments.length === 0 && !captainsLoading && (
                <TableRow>
                  <TableCell colSpan={readOnly ? 7 : 8}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      {captainAssignments.length === 0 ? 'No team access assignments for this team.' : 'No team access assignments match these filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          {onBack && (
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 0.75 }}>
              Back
            </Button>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Teams</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage team setup, roster, and access from one team-specific workspace.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
          {!readOnly && <Button variant="contained" startIcon={<AddIcon />} onClick={createTeam}>Add Team</Button>}
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
        {teamsLoading && <LinearProgress sx={{ mb: 1 }} />}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {([
              ['active', 'Active Teams'],
              ['inactive', 'Inactive Teams'],
              ['all', 'All Teams'],
            ] as Array<[TeamStatusFilter, string]>).map(([value, label]) => (
              <Button key={value} size="small" variant={teamStatusFilter === value ? 'contained' : 'outlined'} onClick={() => setTeamFilter(value)}>
                {label}
              </Button>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button size="small" variant="outlined" disabled={selectedTeamIndex <= 0} onClick={() => selectAdjacentTeam(-1)}>Previous</Button>
            <Button size="small" variant="outlined" disabled={selectedTeamIndex < 0 || selectedTeamIndex >= visibleTeams.length - 1} onClick={() => selectAdjacentTeam(1)}>Next</Button>
          </Stack>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1.25, overflowX: 'auto', pb: 0.5 }}>
          {visibleTeams.map((team) => {
            const summary = teamSummaries.get(team.id);
            const selected = selectedTeamId === team.id;
            return (
              <Paper
                key={team.id}
                variant="outlined"
                onClick={() => selectTeam(team.id, activeTab)}
                sx={{
                  p: 1.5,
                  minWidth: 280,
                  cursor: 'pointer',
                  borderColor: selected ? 'success.main' : 'divider',
                  bgcolor: selected ? 'rgba(46, 125, 50, 0.08)' : 'background.paper',
                  boxShadow: selected ? 2 : 0,
                }}
              >
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{team.team_name}</Typography>
                    <Chip size="small" color={team.is_active ? 'success' : 'default'} label={team.is_active ? 'Active' : 'Inactive'} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {[team.season_label, teamTypeLabel(team.team_gender_type), `${summary?.rosterCount ?? 0} players`].filter(Boolean).join(' • ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {team.league_name || lookupLabel(lookups, 'league_configurations', team.league_configuration_id)}
                    {team.team_flight ? ` • Flight ${team.team_flight}` : ''}
                  </Typography>
                </Stack>
              </Paper>
            );
          })}
          {!readOnly && (
            <Paper
              variant="outlined"
              onClick={createTeam}
              sx={{ p: 1.5, minWidth: 180, cursor: 'pointer', display: 'grid', placeItems: 'center', borderStyle: 'dashed' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AddIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Add Team</Typography>
              </Stack>
            </Paper>
          )}
        </Box>
        {visibleTeams.length === 0 && !teamsLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {teams.length === 0 ? 'No teams found.' : 'No teams match this status filter.'}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 72, zIndex: 3, bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedTeam?.team_name || 'New Team'}</Typography>
              <Chip size="small" color={selectedTeam?.is_active === false ? 'default' : 'success'} label={selectedTeam?.is_active === false ? 'Inactive' : 'Active'} />
            </Stack>
            {selectedTeam && (
              <Typography variant="body2" color="text.secondary">
                {[selectedTeam.season_label, lookupLabel(lookups, 'organizations', selectedTeam.organization_id), teamTypeLabel(selectedTeam.team_gender_type)].filter(Boolean).join(' • ')}
                {selectedTeam.team_flight ? ` • Flight ${selectedTeam.team_flight}` : ''} • {selectedSummary?.rosterCount ?? 0} players • Captain: {joinNames(selectedSummary?.captainNames || [])} • Co-Captains: {joinNames(selectedSummary?.coCaptainNames || [])}
              </Typography>
            )}
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {availableTabs.map((tab) => (
                <Button key={tab} variant={activeTab === tab ? 'contained' : 'outlined'} onClick={() => setTeamTab(tab)} disabled={!selectedTeamId && tab !== 'overview'}>
                  {teamTabLabels[tab]}
                </Button>
              ))}
              <Button variant="outlined" startIcon={<VisibilityIcon />} disabled={!selectedTeamId} onClick={() => selectedTeamId && onViewTeamMatches(selectedTeamId)}>
                View Matches
              </Button>
              {StagingSeasonSetupEntry && selectedTeam && !readOnly && (seasonAccess.isOwner || seasonAccess.teamAssignments.some(a => a.team_id === selectedTeam.id && a.is_active && ['captain', 'co_captain'].includes(a.assignment_role) && a.matches_lineups_access === 'edit')) && <Suspense fallback={null}><StagingSeasonSetupEntry key={selectedTeam.id} team={selectedTeam} lookups={lookups} userPreferences={userPreferences} refreshLookups={refreshLookups} /></Suspense>}
              {DevelopmentSeasonSetupEntry && selectedTeam && !readOnly && (
                <Suspense fallback={null}>
                  <DevelopmentSeasonSetupEntry key={selectedTeam.id} team={selectedTeam} />
                </Suspense>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'lineup_recommendations' && selectedTeamId && renderLineupRecommendations()}
      {activeTab === 'roster' && selectedTeamId && (
        <RosterManager
          lookups={lookups}
          refreshLookups={async () => {
            await refreshLookups();
            await loadTeams();
          }}
          userPreferences={userPreferences}
          onUserPreferencesChange={onUserPreferencesChange}
          fixedTeamId={selectedTeamId}
          embedded
          readOnly={readOnly}
          view="roster"
          canViewPartnerPreferences={canViewPartnerPreferences}
          canEditPartnerPreferences={canEditPartnerPreferences}
        />
      )}
      {activeTab === 'partner_preferences' && selectedTeamId && (
        <RosterManager
          lookups={lookups}
          refreshLookups={async () => {
            await refreshLookups();
            await loadTeams();
          }}
          userPreferences={userPreferences}
          onUserPreferencesChange={onUserPreferencesChange}
          fixedTeamId={selectedTeamId}
          embedded
          readOnly={readOnly || !canEditPartnerPreferences}
          view="partner_preferences"
          canViewPartnerPreferences={canViewPartnerPreferences}
          canEditPartnerPreferences={canEditPartnerPreferences}
        />
      )}
      {activeTab === 'fees_payments' && selectedTeamId && renderFeesPayments()}
      {activeTab === 'access' && selectedTeamId && renderAccess()}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAssignment ? 'Edit Team Access' : 'Add Team Access'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>User</InputLabel>
              <Select label="User" value={captainForm.app_user_id} onChange={(event) => setCaptainForm((current) => ({ ...current, app_user_id: event.target.value }))}>
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>{userLabel(users, user.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={captainForm.assignment_role}
                onChange={(event) => {
                  const assignmentRole = event.target.value as TeamCaptainAssignmentRecord['assignment_role'];
                  setCaptainForm((current) => ({ ...current, assignment_role: assignmentRole, is_primary_captain: primaryCaptainApplies(assignmentRole) ? current.is_primary_captain : false }));
                }}
              >
                {assignmentRoleOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel disabled={!primaryCaptainApplies(captainForm.assignment_role)} control={<Checkbox checked={primaryCaptainApplies(captainForm.assignment_role) && captainForm.is_primary_captain} onChange={(event) => setCaptainForm((current) => ({ ...current, is_primary_captain: event.target.checked }))} />} label="Primary Captain" />
            <FormControlLabel control={<Checkbox checked={captainForm.is_active} onChange={(event) => setCaptainForm((current) => ({ ...current, is_active: event.target.checked }))} />} label="Active" />
            <TextField label="Notes" value={captainForm.notes} multiline minRows={3} onChange={(event) => setCaptainForm((current) => ({ ...current, notes: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveCaptain()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(teamUpdatesPreview)} onClose={() => setTeamUpdatesPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Preview GroupMe Team Update</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2"><strong>Destination:</strong> {teamUpdatesPreview?.destination || '—'}</Typography>
            <Typography variant="body2"><strong>Bot:</strong> {teamUpdatesPreview?.bot || '—'}</Typography>
            {teamUpdatesPreview?.noContent ? <Alert severity="info">No upcoming player-visible events found. Nothing will be posted.</Alert> : (
              <Paper variant="outlined" sx={{ p: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', bgcolor: 'background.default' }}>
                <Typography component="pre" variant="body2" sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>{teamUpdatesPreview?.message}</Typography>
              </Paper>
            )}
            {teamUpdatesPreview?.warning && <Alert severity="warning">{teamUpdatesPreview.warning}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamUpdatesPreview(null)}>Cancel</Button>
          {!teamUpdatesPreview?.noContent && <Button variant="contained" disabled={teamUpdatesSaving} onClick={() => void sendTeamUpdate(Boolean(teamUpdatesPreview?.warning))}>{teamUpdatesPreview?.warning ? 'Post Again' : 'Send to GroupMe'}</Button>}
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
